import { Check } from "@mui/icons-material";
import React from "react";
import styled from "styled-components";
import CloseIcon from "../../assets/icons/CloseIcon";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { useTheme } from "../../middleware/ThemeContext";
import CancelFormEditIcon from "../../assets/icons/CancelFormEditIcon";

// ✅ Reuse Overlay
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

// ✅ Reuse Container but allow background from theme
const Container = styled("div")(({ theme }) => ({
    width: "100%",
    maxWidth: "600px",
    background: theme.CorporateTheme.colors.background,
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
    textAlign: "center",
    fontFamily: '"Segoe UI", sans-serif',
}));

const BigIconWrapper = styled.div`
  background-color: ${({theme}) => theme.CorporateTheme.colors.mintBG};
  padding: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Body = styled.div`
  padding: 24px;
`;

const Title = styled("h2")(({ theme }) => ({
    fontSize: "18px",
    fontWeight: 700,
    fontFamily: '"Raleway", sans-serif',
    color: theme.CreateFormTheme.colors.primary,
    marginBottom: "12px",
}));

const Subtitle = styled("p")(({ theme }) => ({
    fontSize: "14px",
    color: theme.CorporateTheme.colors.textSecondary,
    fontWeight: 400,
    lineHeight: 1.5,
    fontFamily: '"Raleway", sans-serif',
}));

const Actions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 24px;
`;

const ActionButton = styled.button<{
    variant: "confirm" | "cancel";
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 16px;
  padding: 7px 16px;
  border: 2px solid ${({ theme }) => theme.CreateFormTheme.colors.border};
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease;
  background-color: ${({ variant, theme }) =>
        variant === "confirm"
            ? theme.CreateFormTheme.colors.primaryBtn
            : theme.CorporateTheme.colors.background};
  color: ${({ variant, theme }) =>
        variant === "confirm"
            ? theme.CorporateTheme.colors.background
            : theme.CorporateTheme.colors.textSecondary};

  label {
    margin-top: 2px;
    font-family: "Raleway", sans-serif;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
  }
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    opacity: 0.9;
    background: ${({ variant, theme }) =>
        variant === "confirm"
            ? theme.CreateFormTheme.colors.hoverPrimary
            : theme.CreateFormTheme.colors.hoverCancel};
  }
`;

interface ConfirmationPromptProps {
    onConfirm: () => void;
    onCancel: () => void;
    title?: string;
    subTitle?: string;
    loading?: boolean;
}

const ConfirmationPrompt: React.FC<ConfirmationPromptProps> = ({
    onConfirm,
    onCancel,
    title,
    subTitle,
    loading,
}) => {
    const { theme } = useTheme();
    return (
        <Overlay>
            <Container theme={theme}>
                <Body>
                    <div className="d-flex justify-content-center align-item-center mb-3">

                    {/* <BigIconWrapper theme={theme}>
                        <Check />
                    </BigIconWrapper> */}
                    <CancelFormEditIcon />
                    </div > 
                    <Title theme={theme}>
                        {title ?? "You might have unsaved changes."}
                    </Title>
                    <Subtitle theme={theme}>
                        {subTitle ??
                            "This action cannot be undone. Please confirm before proceeding."}
                    </Subtitle>
                </Body>

                <Actions>
                    <ActionButton theme={theme} variant="cancel" onClick={onCancel}>
                        <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
                        <label> Cancel</label>
                    </ActionButton>
                    
                    <ActionButton
                        theme={theme}
                        variant="confirm"
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <label>Processing...</label>
                                <LoadingCircleIcon color={theme.CorporateTheme.colors.textSecondary} />
                            </>
                        ) : (
                            <>
                                <Check />
                                <label> Confirm</label>
                            </>
                        )}
                    </ActionButton>
                </Actions>
            </Container>
        </Overlay>
    );
};

export default ConfirmationPrompt;
